import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import { Card } from '../../components/Card';
import { DateRangeFilter } from '../../components/DateRangeFilter';
import { KpiCard } from '../../components/KpiCard';
import { Pill } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { dashboardApi } from '../../lib/apiClient';
import { dayToIso } from '../../lib/dateRange';
import { fmtAmount, fmtInt } from '../../lib/format';
import { useDateRange } from '../../lib/useDateRange';
import { useUi } from '../../stores/ui';
import { OperationsHeader } from './OperationsHeader';

type EvidenceItem = Awaited<
  ReturnType<typeof dashboardApi.getEvidenceVerificationReport>
>['items'][number];
type EvidenceAlert = Awaited<
  ReturnType<typeof dashboardApi.getEvidenceVerificationReport>
>['alerts'][number];

const STATUS_TONE = {
  verified: 'ok',
  missing: 'neutral',
  hash_mismatch: 'danger',
  failed: 'warn',
} as const;

const ALERT_TONE = {
  hash_mismatch: 'danger',
  upload_failed: 'warn',
} as const;

const ALERT_LABEL = {
  hash_mismatch: 'ไฟล์ไม่ตรงกัน',
  upload_failed: 'อัปโหลดไม่สำเร็จ',
} as const;

function fmtOwnerType(ownerType: EvidenceItem['ownerType']): string {
  return ownerType === 'payment' ? 'รับชำระ' : 'จ่ายเงินสด';
}

function fmtStatus(status: EvidenceItem['status']): string {
  if (status === 'verified') return 'ตรวจสอบแล้ว';
  if (status === 'missing') return 'ไม่มีหลักฐาน';
  if (status === 'hash_mismatch') return 'ไฟล์ไม่ตรงกัน';
  return 'อัปโหลดไม่สำเร็จ';
}

export function EvidenceVerificationView() {
  const branchId = useUi((state) => state.branchId);
  const { range } = useDateRange();
  const { data, isLoading } = useQuery({
    queryKey: ['evidence-verification', branchId, range.fromDay, range.toDay],
    queryFn: () =>
      dashboardApi.getEvidenceVerificationReport({
        from: dayToIso(range.fromDay),
        to: dayToIso(range.toDay),
        branchId: branchId ?? undefined,
      }),
  });

  const itemColumns = useMemo<ColumnDef<EvidenceItem, unknown>[]>(
    () => [
      { accessorKey: 'date', header: 'วันที่' },
      { accessorKey: 'branchId', header: 'สาขา' },
      {
        accessorKey: 'ownerType',
        header: 'ประเภท',
        cell: (cell) =>
          fmtOwnerType(cell.getValue<EvidenceItem['ownerType']>()),
      },
      { accessorKey: 'ownerId', header: 'อ้างอิง' },
      {
        accessorKey: 'amount',
        header: 'ยอด',
        cell: (cell) => (
          <span className="num">{fmtAmount(cell.getValue<number>())}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'สถานะ',
        cell: (cell) => {
          const status = cell.getValue() as EvidenceItem['status'];
          return <Pill tone={STATUS_TONE[status]}>{fmtStatus(status)}</Pill>;
        },
      },
      {
        accessorKey: 'lastErrorMessage',
        header: 'ข้อผิดพลาดล่าสุด',
        cell: (cell) => cell.getValue<string | undefined>() ?? '—',
      },
    ],
    [],
  );

  const alertColumns = useMemo<ColumnDef<EvidenceAlert, unknown>[]>(
    () => [
      { accessorKey: 'date', header: 'วันที่' },
      { accessorKey: 'branchId', header: 'สาขา' },
      {
        accessorKey: 'alertType',
        header: 'ประเภท',
        cell: (cell) => {
          const type = cell.getValue() as EvidenceAlert['alertType'];
          return <Pill tone={ALERT_TONE[type]}>{ALERT_LABEL[type]}</Pill>;
        },
      },
      { accessorKey: 'ownerId', header: 'อ้างอิง' },
      { accessorKey: 'message', header: 'ข้อความ' },
    ],
    [],
  );

  const summary = data?.summary;

  return (
    <div className="bo-fade">
      <OperationsHeader
        title="ตรวจหลักฐานโอน"
        subtitle="ตรวจความครบถ้วนและความถูกต้องของไฟล์หลักฐาน เพื่อปิดรายการผิดปกติก่อนสรุปยอด"
        status={
          isLoading
            ? { label: 'กำลังตรวจสอบหลักฐาน', tone: 'neutral' }
            : (summary?.hashMismatch ?? 0) + (summary?.failed ?? 0) > 0
              ? {
                  label: `พบ ${fmtInt((summary?.hashMismatch ?? 0) + (summary?.failed ?? 0))} รายการผิดปกติ`,
                  tone: 'danger',
                }
              : (summary?.missing ?? 0) > 0
                ? {
                    label: `รอหลักฐาน ${fmtInt(summary?.missing ?? 0)} รายการ`,
                    tone: 'warn',
                  }
                : { label: 'หลักฐานครบถ้วน', tone: 'ok' }
        }
      />
      <DateRangeFilter />
      <div className="grid gap-4 md:grid-cols-5 mb-6">
        <KpiCard
          label="ต้องมีหลักฐาน"
          value={isLoading ? '…' : fmtInt(summary?.required ?? 0)}
        />
        <KpiCard
          label="ตรวจสอบแล้ว"
          value={isLoading ? '…' : fmtInt(summary?.verified ?? 0)}
          tone="ok"
        />
        <KpiCard
          label="ยังไม่มีหลักฐาน"
          value={isLoading ? '…' : fmtInt(summary?.missing ?? 0)}
          tone="warn"
        />
        <KpiCard
          label="ไฟล์ไม่ตรงกัน"
          value={isLoading ? '…' : fmtInt(summary?.hashMismatch ?? 0)}
          tone="danger"
        />
        <KpiCard
          label="อัปโหลดไม่สำเร็จ"
          value={isLoading ? '…' : fmtInt(summary?.failed ?? 0)}
          tone="warn"
        />
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="รายการที่ต้องมีหลักฐาน">
          <DataTable
            columns={itemColumns}
            data={data?.items ?? []}
            pageSize={10}
            emptyMessage="ไม่มีรายการที่ต้องตรวจ"
          />
        </Card>
        <Card title="รายการที่ควรตรวจสอบ">
          <DataTable
            columns={alertColumns}
            data={data?.alerts ?? []}
            pageSize={10}
            emptyMessage="ไม่มี alert"
          />
        </Card>
      </div>
    </div>
  );
}
